/**
 * marketIntel — pure logic behind the Market Calendar's Macro/Earnings subtabs
 * and the news feed: indicator classification, the deterministic plain-English
 * explanations ("CPI 3.4% vs 3.2% expected — hotter than forecast"), grouping
 * and filtering. No fetches, no dates-from-now (callers pass `today`), so the
 * whole module is golden-testable (marketIntelCalc.test.ts).
 */
import type { EconEvent, EarningsEvent, NewsItem } from '@/lib/marketIntelApi';

// -- indicator classification -------------------------------------------------
export interface IndicatorRule {
  /** Substring matchers against the lowercased event type. */
  match: RegExp;
  /** Short category label shown as a chip (Inflation, Jobs, Growth…). */
  category: string;
  /**
   * Which direction of an actual-vs-forecast surprise this explanation frames
   * as "hotter/stronger": 'higher' means a higher-than-expected print reads as
   * a stronger/hotter release.
   */
  strongerWhen: 'higher' | 'lower';
  /** One clause of context appended to the surprise sentence. */
  context: string;
}

/**
 * Ordered — first match wins, so specific types (Core CPI) precede the
 * generic buckets. Every category here also counts as a "key event" for the
 * default Macro filter.
 */
const INDICATOR_RULES: IndicatorRule[] = [
  { match: /cpi|inflation rate|ppi|pce|price index|deflator/, category: 'Inflation', strongerWhen: 'higher',
    context: 'hotter inflation pressures rates higher for longer; cooler prints support rate cuts' },
  // Claims count people LOSING jobs — higher is a WEAKER labour market, the
  // opposite polarity of payrolls. Must precede the hiring rule below.
  { match: /jobless|unemployment claims|initial claims|continuing claims/, category: 'Jobs', strongerWhen: 'lower',
    context: 'rising claims signal a softening labour market' },
  { match: /unemployment/, category: 'Jobs', strongerWhen: 'lower',
    context: 'lower unemployment signals a tighter labour market' },
  { match: /nonfarm|payroll|employment change|\bjobs\b/, category: 'Jobs', strongerWhen: 'higher',
    context: 'stronger hiring supports growth but can delay rate cuts' },
  { match: /interest rate|rate decision|bank rate|refinancing|fed funds|repo rate/, category: 'Rates', strongerWhen: 'higher',
    context: 'policy-rate moves reprice bonds, currencies and rate-sensitive equities' },
  { match: /gdp/, category: 'Growth', strongerWhen: 'higher',
    context: 'faster growth supports earnings; misses raise slowdown concerns' },
  // \b guards: bare 'ism'/'pmi' must not substring-match Optimism/Tourism.
  { match: /\bpmi\b|\bism\b|manufacturing index|services index|business confidence/, category: 'Activity', strongerWhen: 'higher',
    context: 'above 50 signals expansion, below 50 contraction' },
  { match: /retail sales|consumer spending/, category: 'Consumer', strongerWhen: 'higher',
    context: 'consumer spending drives the largest share of demand' },
  { match: /consumer confidence|sentiment/, category: 'Consumer', strongerWhen: 'higher',
    context: 'confidence leads spending intentions' },
  { match: /industrial production|factory orders|durable goods/, category: 'Activity', strongerWhen: 'higher',
    context: 'a read on the industrial cycle' },
  { match: /trade balance|current account|exports|imports/, category: 'Trade', strongerWhen: 'higher',
    context: 'trade flows move currencies and GDP arithmetic' },
  { match: /housing|building permits|home sales|mortgage/, category: 'Housing', strongerWhen: 'higher',
    context: 'housing is the most rate-sensitive sector' },
];

export interface IndicatorInfo {
  category: string;
  key: boolean;
  rule: IndicatorRule | null;
}

export function classifyIndicator(type: string): IndicatorInfo {
  const t = (type || '').toLowerCase();
  for (const rule of INDICATOR_RULES) {
    if (rule.match.test(t)) return { category: rule.category, key: true, rule };
  }
  return { category: 'Other', key: false, rule: null };
}

// -- deterministic explanations -----------------------------------------------
/** Compact number for macro prints — trims float noise, keeps sign. */
export function fmtMacroValue(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const digits = abs >= 1000 ? 0 : abs >= 10 ? 1 : 2;
  return v.toLocaleString('en-US', { maximumFractionDigits: digits });
}

/**
 * One factual sentence (+ context clause) about an econ print. Never advice:
 * describes the surprise direction and what that kind of release tends to mean.
 */
export function explainEconEvent(ev: EconEvent): string {
  const info = classifyIndicator(ev.type);
  const a = ev.actual;
  const e = ev.estimate;
  const p = ev.previous;
  const name = ev.period ? `${ev.type} (${ev.period})` : ev.type;

  if (a === null || a === undefined) {
    const parts = [`${name} is scheduled`];
    if (e !== null && e !== undefined) parts.push(`consensus forecast ${fmtMacroValue(e)}`);
    if (p !== null && p !== undefined) parts.push(`previous ${fmtMacroValue(p)}`);
    let s = `${parts.join(' — ')}.`;
    if (info.rule) s += ` ${capitalize(info.rule.context)}.`;
    return s;
  }

  let verdict = '';
  if (e !== null && e !== undefined) {
    if (a === e) {
      verdict = 'in line with the forecast';
    } else {
      const hotter = a > e ? 'higher' : 'lower';
      const stronger = info.rule ? (hotter === info.rule.strongerWhen) : a > e;
      verdict = `${a > e ? 'above' : 'below'} the ${fmtMacroValue(e)} forecast — ${
        info.category === 'Inflation'
          ? (a > e ? 'hotter than expected' : 'cooler than expected')
          : (stronger ? 'stronger than expected' : 'weaker than expected')
      }`;
    }
  } else if (p !== null && p !== undefined && a !== p) {
    verdict = `${a > p ? 'up' : 'down'} from ${fmtMacroValue(p)} previously`;
  }

  let s = `${name} came in at ${fmtMacroValue(a)}`;
  if (verdict) s += `, ${verdict}`;
  if (p !== null && p !== undefined && e !== null && e !== undefined) s += ` (previous ${fmtMacroValue(p)})`;
  s += '.';
  if (info.rule) s += ` ${capitalize(info.rule.context)}.`;
  return s;
}

/**
 * The EPS surprise percent: stored surprisePct, else derived from
 * actual-vs-estimate. THE one implementation — the Earnings table chip and
 * explainEarnings both read it, so they can never disagree.
 */
export function earningsSurprisePct(ev: EarningsEvent): number | null {
  if (ev.epsActual == null || ev.epsEstimate == null) return null;
  if (ev.surprisePct != null) return ev.surprisePct;
  if (ev.epsEstimate === 0) return null;
  return ((ev.epsActual - ev.epsEstimate) / Math.abs(ev.epsEstimate)) * 100;
}

/** Beat/miss framing for a reported quarter; schedule framing for an upcoming one. */
export function explainEarnings(ev: EarningsEvent): string {
  const cur = ev.currency ? `${ev.currency} ` : '';
  const when =
    ev.beforeAfter === 'BeforeMarket' ? ' before the open'
    : ev.beforeAfter === 'AfterMarket' ? ' after the close'
    : '';
  if (ev.epsActual === null || ev.epsActual === undefined) {
    const est = ev.epsEstimate !== null && ev.epsEstimate !== undefined
      ? ` Consensus expects EPS of ${cur}${ev.epsEstimate.toFixed(2)}.`
      : '';
    return `${ev.ticker} reports on ${ev.reportDate}${when}.${est}`;
  }
  const actual = `${cur}${ev.epsActual.toFixed(2)}`;
  if (ev.epsEstimate === null || ev.epsEstimate === undefined) {
    return `${ev.ticker} reported EPS of ${actual}${when ? ` on ${ev.reportDate}${when}` : ''}.`;
  }
  const est = `${cur}${ev.epsEstimate.toFixed(2)}`;
  const pct = earningsSurprisePct(ev);
  const pctTxt = pct !== null ? ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
  const verdict = ev.epsActual > ev.epsEstimate ? 'a beat' : ev.epsActual < ev.epsEstimate ? 'a miss' : 'in line';
  return `${ev.ticker} reported EPS of ${actual} vs ${est} expected${pctTxt} — ${verdict}.`;
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

// -- grouping / filtering -----------------------------------------------------
export interface EconFilter {
  country: string;      // '' = all
  keyOnly: boolean;
  search: string;
}

export function filterEconEvents(events: EconEvent[], f: EconFilter): EconEvent[] {
  const q = f.search.trim().toLowerCase();
  return events.filter((ev) => {
    if (f.country && ev.country !== f.country) return false;
    if (f.keyOnly && !classifyIndicator(ev.type).key) return false;
    if (q && !`${ev.type} ${ev.country}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** Ordered [date, rows[]] pairs preserving the rows' existing order. */
export function groupByDate<T>(rows: T[], dateOf: (r: T) => string): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const d = dateOf(r);
    const list = map.get(d);
    if (list) list.push(r);
    else map.set(d, [r]);
  }
  return [...map.entries()];
}

/**
 * Sort key for a news item: date-only items (the nightly per-ticker mirror
 * carries no publication time) rank at END-of-day, so same-day holdings news
 * interleaves at the top of the day instead of sinking below every
 * timestamped RSS headline ('YYYY-MM-DD' < 'YYYY-MM-DDT…' lexicographically).
 */
function newsSortKey(it: NewsItem): string {
  return it.publishedAt || `${it.date}T23:59:59.999Z`;
}

/**
 * Combined news stream for the feed views: market-wide headlines + per-ticker
 * items, newest first. Dedupe is by source LINK (the two pipelines mint
 * different id schemes for the same article — djb2 vs sha1 — so ids can
 * never collide across streams); id is the fallback for linkless items.
 * `sym` filter matches per-ticker items.
 */
export function mergeNewsStreams(headlines: NewsItem[], tickerNews: NewsItem[], sym = ''): NewsItem[] {
  const byKey = new Map<string, NewsItem>();
  for (const it of [...headlines, ...tickerNews]) {
    const key = it.link.trim().toLowerCase() || it.id;
    const prev = byKey.get(key);
    if (!prev) byKey.set(key, it);
    // Same article in both streams: keep the first (RSS carries the real
    // timestamp + source name) but adopt the ticker tag so the sym filter
    // still finds it.
    else if (!prev.sym && it.sym) byKey.set(key, { ...prev, sym: it.sym });
  }
  let items = [...byKey.values()];
  if (sym) items = items.filter((it) => (it.sym || '').toUpperCase() === sym.toUpperCase());
  return items.sort((a, b) => newsSortKey(b).localeCompare(newsSortKey(a)));
}

/**
 * Compact calendar-chip label for an econ event type: strips boilerplate
 * suffixes ("Rate", "Index"… stay — only noise like "(MoM)" markers move to
 * the period) and hard-caps the length so day cells don't overflow.
 */
export function shortEventLabel(type: string, max = 16): string {
  const t = (type || '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

/** "3h ago" / "2d ago" stamps for feed rows and the last-updated chip. */
export function relativeTime(thenMs: number | null | undefined, nowMs: number): string {
  if (!thenMs) return '';
  const mins = Math.max(0, Math.round((nowMs - thenMs) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The market brief's label: WHEN it was written, in UTC, and WHY — the
 * scheduled 07:00 / 21:00 brief, or a rewrite for a major story (news step
 * N4). A brief with no `reason` predates N4 and was written from headlines.
 */
export function briefLabel(brief: { generatedAt: number; reason?: string }, nowMs: number): string {
  const d = new Date(brief.generatedAt);
  const hhmm = d.toISOString().slice(11, 16);
  // A fixed month list, not toLocaleDateString: ICU versions disagree ("Sep" / "Sept").
  const day = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  const why = !brief.reason
    ? "from the day's headlines"
    : brief.reason === 'scheduled'
      ? "the scheduled brief, from the day's top stories"
      : `rewritten for a major story: ${brief.reason.replace(/^major: /, '')}`;
  return `Written ${hhmm} UTC, ${day} (${relativeTime(brief.generatedAt, nowMs)}) — ${why}.`;
}
